/**
 * The berth store (M8 plan MC-b4; MC-b5 adds its access rules): free, death-safe personal storage at every dockable
 * station, `career.storage[stationId]`. `features/berth/feature.ts` registers `berth.store` and `berth.take` on the
 * craft hook.
 *
 * - **Free (T21):** no rent, fee or tonnage cap. Only stored components are capped (`career.json berth`), per station
 *   and over every berth, which bounds the save.
 * - **The pilot's, not a ship's:** it lives on the Career, so death (`applyDeath`) and `boardShip` leave it alone.
 *   It is not M4 station stock: nothing here reaches the strategic sim or a `StationView`.
 * - **Refused (T42, decision 6):** components with a `contractId`, `sealed` or `story` (they belong to their mission)
 *   and `contraband` (it stays in the hold and keeps its scan risk).
 * - **Conservation:** a store or take moves exactly what leaves one side into the other, or refuses and changes
 *   nothing. `take` is all or nothing against the free hold (`cargoFreeT`) and the flown ship's largest part
 *   (`maxPartMassT` of its cargo racks; `career.ship` is always the flown ship).
 * - An emptied berth is dropped (and `storage` with it), so its `owner` is stamped again at the next first deposit.
 *
 * **Access (MC-b5, T21):** goods are never deleted. `berthAccess` derives a berth's state from its station's cached
 * `StationView` (no career-side event exists): **frozen** while the station is disabled (store and take refused; the
 * goods wait), **relocating** once access is lost (a capture, an undockable station, a reverted hulk, or docking
 * refused: hostile standing or the `dockingRefused` retaliation row), else **open**. `berthsOnDock` (the runtime's one
 * call in `onDocked`) stamps `relocateAfter` once, clears it when access comes back before it is due, and moves a due
 * berth for free to the nearest open station in the same system, else the nearest by relay hops (`bfsHops`).
 * **Player-corp stations:** a station the corporation owns is always the pilot's. Its view owner `'player'` matches any
 * stamp (a berth stamped before the view listed the station carries the corp's stand-in site faction,
 * `factions.json corporation.siteFaction`), and the stamp is rewritten to `'player'`, so a later capture still reads
 * as one.
 */
import { m2StationId } from '../bridge/m2/stations'
import { moduleStats } from '../data/m2'
import { WARE_IDS, type M2Data, type StationId, type WareId } from '../data/m2Schemas'
import lawJson from '../data/strategic/law.json'
import strategicJson from '../data/strategic/strategic.json'
import { retaliationRefuses } from '../story/refusals'
import { isRevertedHulk } from '../strategic-link/stations'
import { PLAYER_FACTION_ID, type CorpFactionId, type StationView } from '../strategic/types'
import { containerHoldT, goodHoldT, wareHoldT } from './hullFit'
import { EPSILON, cargoFreeT, withComponent, withGood, withoutComponent, withWare } from './ops'
import { goodT, isGoodId, type GoodId, type Goods } from './parts'
import { cargoFull, fail, formatTonnes, invalidAmount, ok, type CmdFailure, type CmdResult } from './result'
import type { BerthEntry, CargoComponent, Career } from './types'

/** What to move between the hold and a berth. */
export interface BerthSelection {
  goods?: Goods
  wares?: Partial<Record<WareId, number>>
  /** `CargoComponent.id`s. */
  components?: string[]
}

declare module '../craft/hook' {
  interface CraftCommandMap {
    /** Stores `sel` from the hold in the berth at `stationId` (the docked station). */
    'berth.store': { kind: 'berth.store'; stationId: StationId; sel: BerthSelection }
    /** Takes `sel` from the berth at `stationId` (the docked station) into the hold. */
    'berth.take': { kind: 'berth.take'; stationId: StationId; sel: BerthSelection }
  }
}

/** The data the berth reads: the caps (`career.berth`) and the flown cargo racks' `maxPartMassT`. */
export type BerthData = Pick<M2Data, 'career' | 'modules'>

/** Why a component can't go into a berth. */
export type BerthRefusal = 'contract' | 'sealed' | 'story' | 'contraband'

/** The player-facing subject of each refusal ("… can't be stored in a berth."). */
export const BERTH_REFUSAL_TEXT: Readonly<Record<BerthRefusal, string>> = Object.freeze({
  contract: 'Contract cargo',
  sealed: 'Sealed cargo',
  story: 'Mission items',
  contraband: 'Contraband',
})

/** Why `c` can't be stored (T42, decision 6), or null when it can. The berth panel (MC-b6a) disables the row with it. */
export function berthRefusal(c: Pick<CargoComponent, 'contractId' | 'sealed' | 'story' | 'contraband'>): BerthRefusal | null {
  if (c.contractId !== undefined) return 'contract'
  if (c.story === true) return 'story'
  if (c.sealed === true) return 'sealed'
  if (c.contraband === true) return 'contraband'
  return null
}

/** The berth at `stationId`, or null when nothing is stored there. */
export function berthAt(career: Career, stationId: StationId): BerthEntry | null {
  return career.storage?.[stationId] ?? null
}

/** Components stored over every berth (the total cap's count). */
export function berthComponentCount(career: Career): number {
  let n = 0
  const storage = career.storage
  if (storage !== undefined) for (const id of Object.keys(storage)) n += storage[id].components.length
  return n
}

/** Is a berth empty (no amount above `EPSILON`, no component)? */
export function berthEmpty(entry: BerthEntry): boolean {
  if (entry.components.length > 0) return false
  for (const q of Object.values(entry.goods)) if ((q ?? 0) > EPSILON) return false
  for (const t of Object.values(entry.wares)) if ((t ?? 0) > EPSILON) return false
  return true
}

// ---- Selection ------------------------------------------------------------------------------

interface ParsedSelection {
  goods: [GoodId, number][]
  wares: [WareId, number][]
  components: string[]
}

function validQty(q: unknown): q is number {
  return typeof q === 'number' && Number.isFinite(q) && q > 0
}

function isWareId(w: string): w is WareId {
  return (WARE_IDS as readonly string[]).includes(w)
}

/** Checks a selection; refuses an empty one, a bad amount, an unknown id or a component selected twice. */
function parseSelection(sel: BerthSelection): ParsedSelection | CmdFailure {
  const out: ParsedSelection = { goods: [], wares: [], components: [] }
  for (const [g, q] of Object.entries(sel.goods ?? {})) {
    if (!isGoodId(g)) return fail('invalidAmount', `Unknown good: ${g}.`, { good: g })
    if (!validQty(q)) return invalidAmount('quantity', Number(q))
    out.goods.push([g, q])
  }
  for (const [w, q] of Object.entries(sel.wares ?? {})) {
    if (!isWareId(w)) return fail('invalidAmount', `Unknown ware: ${w}.`, { ware: w })
    if (!validQty(q)) return invalidAmount('tonnage', Number(q))
    out.wares.push([w, q])
  }
  const ids = sel.components ?? []
  if (new Set(ids).size !== ids.length) return fail('invalidAmount', 'A component is selected twice.')
  out.components = [...ids]
  if (out.goods.length + out.wares.length + out.components.length === 0) return invalidAmount('selection', 0)
  return out
}

/** `have` when `q` is at or within `EPSILON` above it (so exactly what is there moves), else `q`. */
function clampTo(q: number, have: number): number {
  return q < have ? q : have
}

/** `rec` with `key` raised by `delta`; the key is dropped once it falls within `EPSILON` of zero. */
function addTo<K extends string>(rec: Partial<Record<K, number>>, key: K, delta: number): Partial<Record<K, number>> {
  const next = { ...rec }
  const t = (rec[key] ?? 0) + delta
  if (t < EPSILON) Reflect.deleteProperty(next, key)
  else next[key] = t
  return next
}

/** The Career with the berth at `stationId` replaced (an empty berth is dropped, and an empty `storage` too). */
function withBerth(career: Career, stationId: StationId, entry: BerthEntry): Career {
  const storage = { ...career.storage }
  if (berthEmpty(entry)) Reflect.deleteProperty(storage, stationId)
  else storage[stationId] = entry
  const next: Career = { ...career, storage }
  if (Object.keys(storage).length === 0) Reflect.deleteProperty(next, 'storage')
  return next
}

// ---- Commands -------------------------------------------------------------------------------

/**
 * Moves `sel` from the hold into the berth at `stationId`. `owner` is the station's `StationView.owner`, stamped at
 * the first deposit (MC-b5 compares it). All or nothing: refuses more than the hold carries (`notInCargo`), a
 * refused component (`serviceUnavailable`, `detail.reason` = its `BerthRefusal`) and a full berth (`cargoFull`,
 * `detail.cap`). `access` (MC-b5, `berthAccess`; default open): a frozen or relocating berth refuses
 * (`serviceUnavailable`, `detail.access`).
 */
export function store(
  career: Career,
  data: BerthData,
  stationId: StationId,
  sel: BerthSelection,
  owner: CorpFactionId,
  access: BerthAccess = BERTH_OPEN,
): CmdResult<Career> {
  const closed = accessRefusal(access, stationId)
  if (closed !== null) return closed
  const parsed = parseSelection(sel)
  if ('ok' in parsed) return parsed
  const caps = data.career.berth
  const entry: BerthEntry = berthAt(career, stationId) ?? { owner, goods: {}, wares: {}, components: [] }
  let hold = career
  let goods = entry.goods
  let wares = entry.wares
  for (const [g, q] of parsed.goods) {
    const held = goodT(career.cargo.parts, g)
    if (q > held + EPSILON) return fail('notInCargo', `Only ${formatTonnes(held)} of that good in the hold.`, { good: g, held, requested: q })
    const moved = clampTo(q, held)
    hold = withGood(hold, g, -moved)
    goods = addTo(goods, g, moved)
  }
  for (const [w, q] of parsed.wares) {
    const held = career.cargo.wares[w]
    if (q > held + EPSILON) return fail('notInCargo', `Only ${formatTonnes(held)} t of that ware in the hold.`, { ware: w, held, requested: q })
    const moved = clampTo(q, held)
    hold = withWare(hold, w, -moved)
    wares = addTo(wares, w, moved)
  }
  const components: CargoComponent[] = [...entry.components]
  for (const id of parsed.components) {
    const c = career.cargo.components.find((x) => x.id === id)
    if (c === undefined) return fail('notInCargo', 'That component is not in the hold.', { id })
    const refusal = berthRefusal(c)
    if (refusal !== null) return fail('serviceUnavailable', `${BERTH_REFUSAL_TEXT[refusal]} can't be stored in a berth.`, { id, reason: refusal })
    if (components.some((x) => x.id === id)) return fail('invalidAmount', `Component ${id} is already in this berth.`, { id })
    hold = withoutComponent(hold, id)
    components.push(c)
  }
  const adding = parsed.components.length
  if (adding > 0) {
    const stored = entry.components.length
    if (stored + adding > caps.componentCapPerStation) {
      return fail('cargoFull', `This berth holds at most ${caps.componentCapPerStation} components.`, { cap: caps.componentCapPerStation, stored, adding })
    }
    const total = berthComponentCount(career)
    if (total + adding > caps.componentCapTotal) {
      return fail('cargoFull', `Your berths hold at most ${caps.componentCapTotal} components in all.`, { cap: caps.componentCapTotal, stored: total, adding })
    }
  }
  return ok(withBerth(hold, stationId, { ...entry, goods, wares, components }))
}

/**
 * Moves `sel` from the berth at `stationId` into the hold. All or nothing: refuses more than the berth holds
 * (`notInCargo`), a component larger than the flown cargo racks take (`tooLarge`) and more tonnes than the hold has
 * free (`cargoFull`). `access` as in `store`: a frozen or relocating berth refuses and its goods stay.
 */
export function take(career: Career, data: BerthData, stationId: StationId, sel: BerthSelection, access: BerthAccess = BERTH_OPEN): CmdResult<Career> {
  const closed = accessRefusal(access, stationId)
  if (closed !== null) return closed
  const parsed = parseSelection(sel)
  if ('ok' in parsed) return parsed
  const entry = berthAt(career, stationId)
  if (entry === null) return fail('notInCargo', 'Nothing is stored in this berth.', { station: stationId })
  const maxPartT = moduleStats(data, 'cargo', career.ship.fitted.cargo).maxPartMassT
  let hold = career
  let goods = entry.goods
  let wares = entry.wares
  let needT = 0
  for (const [g, q] of parsed.goods) {
    const have = goodT(entry.goods, g)
    if (q > have + EPSILON) return fail('notInCargo', `Only ${formatTonnes(have)} of that good in this berth.`, { good: g, have, requested: q })
    const moved = clampTo(q, have)
    goods = addTo(goods, g, -moved)
    hold = withGood(hold, g, moved)
    needT += goodHoldT(career.ship.defId, g, moved) // M10 P5a6b H3: class-weighted (moved × mass today)
  }
  for (const [w, q] of parsed.wares) {
    const have = entry.wares[w] ?? 0
    if (q > have + EPSILON) return fail('notInCargo', `Only ${formatTonnes(have)} t of that ware in this berth.`, { ware: w, have, requested: q })
    const moved = clampTo(q, have)
    wares = addTo(wares, w, -moved)
    hold = withWare(hold, w, moved)
    needT += wareHoldT(career.ship.defId, w, moved)
  }
  let components = entry.components
  for (const id of parsed.components) {
    const c = entry.components.find((x) => x.id === id)
    if (c === undefined) return fail('notInCargo', 'That component is not in this berth.', { id })
    if (career.cargo.components.some((x) => x.id === id)) return fail('invalidAmount', `Component ${id} is already in the hold.`, { id })
    if (c.massT > maxPartT + EPSILON) {
      return fail('tooLarge', `Too large for your cargo racks: ${formatTonnes(c.massT)} t; they take ${formatTonnes(maxPartT)} t.`, {
        id,
        massT: c.massT,
        maxPartMassT: maxPartT,
      })
    }
    components = components.filter((x) => x.id !== id)
    hold = withComponent(hold, c)
    needT += containerHoldT(career.ship.defId, c.massT)
  }
  const free = cargoFreeT(career)
  if (needT > free + EPSILON) return cargoFull(needT, free)
  return ok(withBerth(hold, stationId, { ...entry, goods, wares, components }))
}

// ---- Access and relocation (MC-b5, T21) -----------------------------------------------------

/** A berth's state (`berthAccess`): open, frozen at a disabled station, or relocating at `dueTime` (game seconds). */
export type BerthAccess = { kind: 'open' } | { kind: 'frozen' } | { kind: 'relocating'; dueTime: number }

/** The open state (store and take go ahead). */
export const BERTH_OPEN: BerthAccess = Object.freeze({ kind: 'open' })
const BERTH_FROZEN: BerthAccess = Object.freeze({ kind: 'frozen' })

/** Why access to a berth is lost (it relocates after the delay). */
export type BerthLoss = 'captured' | 'undockable' | 'reverted' | 'refused'

/** The station as the berth rules read it: a cached `StationView`. */
export type BerthStationView = Pick<StationView, 'id' | 'systemId' | 'template' | 'owner' | 'pos' | 'dockable' | 'disabledBy'>

/** Game seconds per day for `relocateDelayDays`: the strategic faction day (`strategic.json clock.dayS`, D-487's day). */
const DAY_S: number = strategicJson.clock.dayS
/** Docking is refused at a standing at or below this (`law.json standing.hostileAt`, `law/docking.ts`). */
const HOSTILE_AT: number = lawJson.standing.hostileAt

/** A strategic site id → the career's station id (`m2IdOfSite` over the live layout; default its slug). */
export type StationIdOf = (siteId: string) => StationId

/**
 * Is `view` the station of the berth keyed `stationId` (the strategic id or its career id)? `idOf` maps the site id
 * (R3-1 sweep: pass `m2IdOfSite` over the live layout so the home Hub's `sys-07-st0` is `hub`; default its slug).
 */
export function isBerthStation(view: Pick<StationView, 'id'>, stationId: StationId, idOf: StationIdOf = m2StationId): boolean {
  return view.id === stationId || idOf(view.id) === stationId
}

function standingWith(career: Career, owner: CorpFactionId): number {
  const s: Partial<Record<string, number>> = career.standings
  return s[owner] ?? 0
}

/**
 * Why the pilot can't use a berth stamped `owner` at `view` (or null). The player corporation's own station is never
 * lost (it never refuses its CEO, D-424) unless it reverted or is undockable; any other owner than the stamp is a
 * capture. Docking refusals mirror `law/docking.ts`: a hostile standing with the owner, or its `dockingRefused`
 * retaliation row (`story/refusals.ts`, D-388). A disabled station is not lost (it shelters; `berthAccess` freezes it).
 */
export function berthLoss(owner: CorpFactionId, view: BerthStationView, career: Career): BerthLoss | null {
  if (isRevertedHulk(view)) return 'reverted'
  if (!view.dockable) return 'undockable'
  if (view.owner === PLAYER_FACTION_ID) return null
  if (view.owner !== owner) return 'captured'
  if (view.disabledBy !== null) return null
  if (standingWith(career, view.owner) <= HOSTILE_AT) return 'refused'
  if (retaliationRefuses(career, { faction: view.owner, template: view.template }, 'dock') !== null) return 'refused'
  return null
}

/**
 * The berth's state from its station's cached view (null: no view, so the stamp alone decides). A reverted hulk, an
 * undockable station, a capture or a docking refusal relocates at `entry.relocateAfter`, or `now` + the delay when not
 * stamped yet; a disabled station freezes (a pending stamp waits); otherwise it is open.
 */
export function berthAccess(entry: BerthEntry, view: BerthStationView | null, career: Career, now: number, data: BerthData): BerthAccess {
  const relocating = (): BerthAccess => ({ kind: 'relocating', dueTime: entry.relocateAfter ?? now + data.career.berth.relocateDelayDays * DAY_S })
  if (view === null) return entry.relocateAfter === undefined ? BERTH_OPEN : relocating()
  if (berthLoss(entry.owner, view, career) !== null) return relocating()
  if (view.disabledBy !== null) return BERTH_FROZEN
  return BERTH_OPEN
}

/** The player-facing refusal of a closed berth, or null when open. */
function accessRefusal(access: BerthAccess, stationId: StationId): CmdFailure | null {
  if (access.kind === 'open') return null
  if (access.kind === 'frozen') {
    return fail('serviceUnavailable', 'This berth is frozen while the station is disabled.', { station: stationId, access: 'frozen' })
  }
  return fail('serviceUnavailable', 'This berth is being relocated.', { station: stationId, access: 'relocating', dueTime: access.dueTime })
}

/** What relocation can see: candidate stations (cached views) and relay hops between systems (−1: unreachable). */
export interface RelocationWorld {
  readonly stations: readonly BerthStationView[]
  hops(fromSystem: string, toSystem: string): number
}

/** A relocation target: the station's view and the relay hops from the lost station's system (0 = same system). */
export interface RelocationTarget {
  view: BerthStationView
  hops: number
}

function dist2(a: readonly number[], b: readonly number[]): number {
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  const dz = a[2] - b[2]
  return dx * dx + dy * dy + dz * dz
}

const ORIGIN: readonly number[] = Object.freeze([0, 0, 0])

/**
 * Where a berth lost at `from` goes: the nearest open station (dockable, not disabled, docking not refused) in the
 * same system, else the one fewest relay hops away (ties: the nearer to its system's origin, then the id). Null when
 * none is reachable; the goods then stay where they are (never deleted) and move once a target exists.
 */
export function relocationTarget(from: BerthStationView, world: RelocationWorld, career: Career): RelocationTarget | null {
  let best: BerthStationView | null = null
  let bestHops = Infinity
  let bestD = Infinity
  for (const v of world.stations) {
    if (v.id === from.id || v.disabledBy !== null || berthLoss(v.owner, v, career) !== null) continue
    const hops = v.systemId === from.systemId ? 0 : world.hops(from.systemId, v.systemId)
    if (hops < 0) continue
    const d = hops === 0 ? dist2(v.pos, from.pos) : dist2(v.pos, ORIGIN)
    const better = hops < bestHops || (hops === bestHops && (d < bestD || (d === bestD && best !== null && v.id < best.id)))
    if (better) {
      best = v
      bestHops = hops
      bestD = d
    }
  }
  return best === null ? null : { view: best, hops: bestHops }
}

/**
 * Moves the whole berth at `fromId` into the berth at `toId` for free (no credit or standing change). The target keeps
 * its owner stamp, or gets `owner` when new; nothing is capped or dropped (relocation never deletes goods).
 */
export function relocate(career: Career, fromId: StationId, toId: StationId, owner: CorpFactionId): Career {
  const from = berthAt(career, fromId)
  if (from === null || fromId === toId) return career
  const to: BerthEntry = berthAt(career, toId) ?? { owner, goods: {}, wares: {}, components: [] }
  let goods = to.goods
  for (const [g, q] of Object.entries(from.goods)) if (isGoodId(g) && (q ?? 0) > 0) goods = addTo(goods, g, q ?? 0)
  let wares = to.wares
  for (const [w, t] of Object.entries(from.wares)) if (isWareId(w) && (t ?? 0) > 0) wares = addTo(wares, w, t ?? 0)
  const components = [...to.components, ...from.components.filter((c) => !to.components.some((x) => x.id === c.id))]
  const storage = { ...career.storage }
  Reflect.deleteProperty(storage, fromId)
  storage[toId] = { ...to, goods, wares, components }
  return { ...career, storage }
}

/** The stations a dock pass sees: the local system's live view. */
export interface BerthSystemView {
  readonly stations: readonly BerthStationView[]
}

/**
 * The dock pass (the runtime's one call in `onDocked`): for every berth whose station `view` (the local system's live
 * view) lists, rewrite a player-corp station's stamp to `'player'`, stamp `relocateAfter` once when access is lost,
 * clear it when access is back, and relocate a due berth (`now` ≥ `relocateAfter`) to `relocationTarget` over `world`
 * (default: the view's own stations, no route to another system). Berths in other systems wait for a view of theirs.
 * Idempotent: running it again on the result, or after a save and load, changes nothing. No view: unchanged. `idOf` maps site ids to berth keys (R3-1).
 */
export function berthsOnDock(
  career: Career,
  data: BerthData,
  view: BerthSystemView | null | undefined,
  now: number,
  world: RelocationWorld | null = null,
  idOf: StationIdOf = m2StationId,
): Career {
  const storage = career.storage
  if (storage === undefined || view === null || view === undefined) return career
  const w: RelocationWorld = world ?? { stations: view.stations, hops: () => -1 }
  let next = career
  for (const id of Object.keys(storage)) {
    const entry = berthAt(next, id)
    const sv = view.stations.find((v) => isBerthStation(v, id, idOf))
    if (entry === null || sv === undefined) continue
    let e: BerthEntry = sv.owner === PLAYER_FACTION_ID && entry.owner !== PLAYER_FACTION_ID ? { ...entry, owner: PLAYER_FACTION_ID } : entry
    const access = berthAccess(e, sv, next, now, data)
    if (access.kind === 'open' && e.relocateAfter !== undefined) {
      e = { ...e }
      Reflect.deleteProperty(e, 'relocateAfter')
    } else if (access.kind === 'relocating' && e.relocateAfter === undefined) {
      e = { ...e, relocateAfter: access.dueTime }
    }
    if (e !== entry) next = { ...next, storage: { ...next.storage, [id]: e } }
    if (access.kind !== 'relocating' || now < access.dueTime) continue
    const target = relocationTarget(sv, w, next)
    if (target !== null) next = relocate(next, id, idOf(target.view.id), target.view.owner)
  }
  return next
}
